import { SkyDialog, SkyMessageBar, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { GRANT_REASON_MIN_LENGTH } from "@shared/types/access";
import type { AccessRow } from "@/hooks/use-access";
import { useMcpEnabled } from "@/hooks/use-mcp";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/** The server's cap on a status change's reason (`userStatusChangeBodySchema`). */
const REASON_MAX_LENGTH = 500;

/**
 * Deactivate a person's account (B-06, FR-11 workforce offboarding), from the Platform tab.
 *
 * Revoking a grant ends that grant only; this ends every one at once, forgets the person's
 * remembered MFA browsers and ends their AI app (MCP) connections (named only while AI apps are
 * on, #1812), and it is undone with
 * "Activate user". The reason is recorded on the audit event, so it is asked for here at the
 * same length as an admin grant's. The dialog stays open until the parent says it landed, so a
 * refusal is read inside it with the reason still typed (the pattern of the other access dialogs).
 */
export function DeactivateUserDialog({
  row,
  isSubmitting,
  onConfirm,
  onOpenChange,
  error = null,
}: {
  /** One grant of the person (the route takes the user id it carries); null while closed. */
  row: AccessRow | null;
  isSubmitting: boolean;
  onConfirm: (row: AccessRow, reason: string) => void;
  onOpenChange: (open: boolean) => void;
  error?: InlineFailure | null;
}) {
  const mcpEnabled = useMcpEnabled();
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Reset during render when a different person opens the dialog, as the reset dialog does.
  const [shownFor, setShownFor] = useState<string | null>(row?.userId ?? null);
  const userId = row?.userId ?? null;
  if (shownFor !== userId) {
    setShownFor(userId);
    setReason("");
    setSubmitted(false);
  }

  const reasonError =
    reason.trim().length < GRANT_REASON_MIN_LENGTH
      ? `Say why, in at least ${GRANT_REASON_MIN_LENGTH} characters. The reason is recorded on the audit event.`
      : reason.trim().length > REASON_MAX_LENGTH
        ? `Keep it to ${REASON_MAX_LENGTH} characters or fewer.`
        : undefined;

  function submit() {
    setSubmitted(true);
    if (!row || reasonError) return;
    onConfirm(row, reason.trim());
  }

  return (
    <SkyDialog
      open={row !== null}
      onOpenChange={onOpenChange}
      // Only a landed deactivation closes it (the parent clears `row`).
      onBeforeClose={(why) => why !== "primaryAction"}
      size="medium"
      testId="deactivate-user-dialog"
      title={row ? `Deactivate ${row.email}` : "Deactivate user"}
      description="Every grant of this person stops working at their next request, and their history stays in the audit trail. You can activate the account again later."
      primaryAction={{
        label: "Deactivate user",
        destructive: true,
        loading: isSubmitting,
        onClick: submit,
      }}
      secondaryAction={{ label: "Keep account active", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-3">
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="This ends all of their access"
          message={`Remembered MFA browsers are forgotten${mcpEnabled ? " and any AI app they connected is disconnected" : ""}. Revoke a single grant instead to keep the account.`}
        />
        <SkyTextarea
          label="Reason"
          required
          value={reason}
          testId="deactivate-user-reason"
          hint={`At least ${GRANT_REASON_MIN_LENGTH} characters, for example "Left the company 2026-10-05".`}
          validationState={submitted && reasonError ? "error" : "none"}
          validationMessage={submitted ? reasonError : undefined}
          onChange={(event) => setReason(event.target.value)}
        />
        <InlineErrorBar
          error={error}
          title={error?.retryable ? undefined : "The account was not deactivated"}
          testId="deactivate-user-error"
        />
      </div>
    </SkyDialog>
  );
}
